import { Alert, Box, Chip, DialogContent, Stack, Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import React, { FC, useMemo } from 'react';
import { BhiDialog, Button, Dropdown, SelectOption } from 'src/UIKit';
import GridData from 'src/components/GridData/GridData';
import { getDateTimeUSAFormatWithTime } from 'src/constants/helpers';
import { SurveyTimePeriodFilter } from 'src/modules/surveyResponses/components/SurveyTimePeriodFilter';
import { useHookDeliveryLogController } from '../../controllers';
import {
  formatDeliveryEntity,
  formatDeliveryResult,
  formatEventType,
  formatPauseDescription,
} from '../../helpers';
import { HookDelivery, HookDeliveryOutcome } from '../../types';
import { ResponseExcerpt } from './HookDeliveryLogDialog.styles';

// The Dropdown does not render an empty value, so "all results" needs a value of its own.
const ALL_OUTCOMES = 'all';

const OUTCOME_OPTIONS: SelectOption<HookDeliveryOutcome | typeof ALL_OUTCOMES>[] = [
  { value: ALL_OUTCOMES, title: 'All results' },
  { value: 'success', title: 'Successful' },
  { value: 'failure', title: 'Failed' },
];

type HookDeliveryLogDialogProps = {
  hookId: string | null;
  companyId: string;
  canEdit: boolean;
  onClose: () => void;
  onHookChanged: () => void;
};

export const HookDeliveryLogDialog: FC<HookDeliveryLogDialogProps> = ({
  hookId,
  companyId,
  canEdit,
  onClose,
  onHookChanged,
}) => {
  const {
    hook,
    deliveries,
    page,
    pageSize,
    sortField,
    outcome,
    period,
    isLoading,
    isActivating,
    activate,
    changeSorting,
    changePagination,
    changeOutcome,
    changePeriod,
  } = useHookDeliveryLogController({ hookId, companyId, onHookChanged });

  const columns: GridColDef<HookDelivery>[] = useMemo(
    () => [
      {
        field: 'dateCreated',
        headerName: 'Time',
        minWidth: 180,
        flex: 1,
        renderCell: ({ row }) => getDateTimeUSAFormatWithTime(new Date(row.dateCreated)),
      },
      {
        field: 'eventType',
        headerName: 'Event',
        minWidth: 170,
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => formatEventType(row.eventType),
      },
      {
        field: 'entityId',
        headerName: 'Related to',
        minWidth: 200,
        flex: 1.5,
        sortable: false,
        renderCell: ({ row }) => (
          <Tooltip title={row.entityId ?? ''}>
            <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {formatDeliveryEntity(row)}
            </Box>
          </Tooltip>
        ),
      },
      {
        field: 'outcome',
        headerName: 'Result',
        minWidth: 190,
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => (
          <Chip
            size="small"
            variant="outlined"
            color={row.outcome === 'success' ? 'success' : 'error'}
            label={formatDeliveryResult(row)}
          />
        ),
      },
      {
        field: 'responseExcerpt',
        headerName: 'Response',
        minWidth: 240,
        flex: 2,
        sortable: false,
        renderCell: ({ row }) => (
          <Tooltip title={row.responseExcerpt ?? ''}>
            <ResponseExcerpt>{row.responseExcerpt ?? '-'}</ResponseExcerpt>
          </Tooltip>
        ),
      },
      {
        field: 'durationMs',
        headerName: 'Duration',
        minWidth: 100,
        sortable: false,
        align: 'right',
        headerAlign: 'right',
        renderCell: ({ row }) => `${row.durationMs.toLocaleString('en-US')} ms`,
      },
    ],
    [],
  );

  const failures = hook?.delivery?.consecutiveFailures ?? 0;
  const pauseThreshold = hook?.delivery?.pauseThreshold;

  return (
    <BhiDialog
      isOpen={Boolean(hookId)}
      onClose={onClose}
      title={hook ? `Delivery log: ${hook.name}` : 'Delivery log'}
      maxWidth="lg"
      fullWidth
    >
      <DialogContent>
        <Stack spacing={2}>
          {hook?.status === 'paused' && (
            <Alert
              severity="warning"
              action={
                canEdit && (
                  <Button color="inherit" size="small" disabled={isActivating} onClick={activate}>
                    Activate
                  </Button>
                )
              }
            >
              {hook.pause ? formatPauseDescription(hook.pause, failures) : 'This webhook is paused.'} Events are not sent
              while the webhook is paused and are not sent later.
            </Alert>
          )}
          {hook?.status === 'active' && failures > 0 && pauseThreshold !== undefined && (
            <Alert severity="info">
              {failures} failed {failures === 1 ? 'delivery' : 'deliveries'} in a row. Only redirects, 4xx answers
              other than 408 and 429, and a URL that points to a non-public address count; the webhook is paused
              automatically after {pauseThreshold}, and a successful delivery resets the count.
            </Alert>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Box sx={{ width: { xs: '100%', sm: 190 } }}>
              <Dropdown
                label="Result"
                value={outcome || ALL_OUTCOMES}
                options={OUTCOME_OPTIONS}
                onChange={(_, value) => changeOutcome(value === ALL_OUTCOMES ? '' : value)}
                margin="none"
                size="small"
              />
            </Box>
            <SurveyTimePeriodFilter value={period} onChange={changePeriod} />
          </Stack>
          <Box sx={{ height: '60vh' }}>
            <GridData
              isLoading={isLoading}
              rows={deliveries.items}
              columns={columns}
              minHeight={300}
              sorting={{ value: sortField, onChange: changeSorting }}
              pagination={{
                page,
                pageSize,
                totalCount: deliveries.count,
                onChange: changePagination,
              }}
            />
          </Box>
        </Stack>
      </DialogContent>
    </BhiDialog>
  );
};
